import { style, globalStyle } from '@vanilla-extract/css'
import { vars, focusRing } from '../design/theme.css'
import {
  birdNameRow,
  kicker as kickerBase,
  reading,
  sciName,
  sectionHead,
  sourceMark,
  sourceMarkHover,
  stateLine,
  utility,
} from '../design/primitives/text.css'

const WIDE = 'screen and (min-width: 820px)'

// ---- Front page container + dateline ----------------------------------
export const front = style({ display: 'flex', flexDirection: 'column', gap: vars.space[6] })

// The dateline row: admin redraw · the big date · listening state.
// The thick-thin rule under a masthead is the oldest signal on a printed page that the folio has
// ended and the news has begun. One border can't do it, so the hairline is a pseudo-element sitting
// in the flex gap below — no extra element, and nothing to keep in sync in the TSX.
export const dateline = style({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: vars.space[3],
  paddingBottom: vars.space[4],
  borderBottom: `3px solid ${vars.color.ink}`,
  selectors: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: '-4px',
      height: '1px',
      background: vars.color.ink,
    },
  },
})
export const datelineSide = utility
export const datelineRight = style([
  datelineSide,
  {
    textAlign: 'right',
    display: 'flex',
    gap: vars.space[2],
    justifyContent: 'flex-end',
    alignItems: 'center',
    '@media': { 'screen and (max-width: 560px)': { display: 'none' } },
  },
])
export const regen = style({
  appearance: 'none',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  color: vars.color.inkSoft,
  padding: vars.space[1],
  selectors: {
    '&:hover:not(:disabled)': { color: vars.color.ink },
    '&:disabled': { opacity: 0.5 },
    '&:focus-visible': focusRing,
  },
})

// ---- The composed lead grid -------------------------------------------
export const grid = style({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: vars.space[6],
  '@media': { [WIDE]: { gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)', gap: vars.space[7] } },
})
export const leadCol = style({ minWidth: 0 })
export const rail = style({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: vars.space[5],
  '@media': { [WIDE]: { borderLeft: `1px solid ${vars.color.rule}`, paddingLeft: vars.space[6] } },
})

// The cut. It follows the headline now rather than heading the page, so it reads as the story's
// picture rather than a hero banner — and its caption hangs off a hairline, the way a printed
// cutline does.
export const leadFig = style({ margin: `0 0 ${vars.space[5]}` })
globalStyle(`${leadFig} img`, { width: '100%', height: 'auto', display: 'block' })
export const leadCaption = style({
  marginTop: vars.space[2],
  paddingTop: vars.space[2],
  borderTop: `1px solid ${vars.color.rule}`,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  color: vars.color.inkSoft,
})
export const leadName = style([
  birdNameRow,
  {
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    selectors: {
      '&:hover': { textDecoration: 'underline', textUnderlineOffset: vars.uline.mark },
      '&:focus-visible': focusRing,
    },
  },
])
// The binomial under the lead picture. It set only a family and a style, so it inherited the
// caption's MONO size and rendered the one always-Latin string on the site at 11px serif.
export const leadSci = sciName
// The photographer's credit, when the lead picture is a licensed photo rather than the station's own
// art. Its own line under the bird's name: a licence condition, not an aside to be crowded out.
export const leadCredit = style({ display: 'block', marginTop: vars.space[1], fontSize: vars.fs.xs })

export const kicker = style([kickerBase, { margin: `0 0 ${vars.space[2]}` }])
// The lead headline — the biggest type on the page (brief §5.4 hierarchy). Set tighter than
// running text on both axes, which is what makes display type read as display type, and balanced
// so a two-line headline doesn't leave one word stranded on the second line.
export const headline = style({
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: 1.04,
  letterSpacing: '-0.022em',
  textWrap: 'balance',
  color: vars.color.ink,
  margin: `0 0 ${vars.space[3]}`,
  '@media': { [WIDE]: { fontSize: vars.fs['3xl'] } },
})
// THE DECK — the second line of a headline, one size down and set roman. The composer writes the
// day's shape as a single string with an em dash in it ("A Dunlin on the shore — a rare visitor to
// Culfin"); printed, those are two ranks of type, not one sentence. Split at the dash and the page
// gains the step from headline to deck to body that a front page is built on. No dash, no deck.
export const deck = style({
  fontFamily: vars.font.serif,
  fontWeight: 400,
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  textWrap: 'pretty',
  color: vars.color.ink2,
  maxWidth: vars.measure.wide,
  margin: 0,
})
// Printed decks are set with a capital even when they continue the headline's sentence. Done in
// CSS rather than in the string so what the composer wrote is what a reader copies.
globalStyle(`${deck}::first-letter`, { textTransform: 'capitalize' })

// The headline block — kicker, headline, deck — ruled off from the cut below it, so the step from
// display type to picture to body is a visible break rather than a change of spacing.
export const leadHead = style({
  margin: `0 0 ${vars.space[5]}`,
  paddingBottom: vars.space[4],
  borderBottom: `1px solid ${vars.color.rule}`,
})
export const note = style({
  fontFamily: vars.font.serif,
  fontStyle: 'italic',
  fontSize: vars.fs.md,
  color: vars.color.ink2,
  margin: `0 0 ${vars.space[4]}`,
})
export const standfirst = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  color: vars.color.ink2,
  maxWidth: vars.measure.prose,
})
// The narrated body — drop-cap first paragraph, ruled columns when there's room.
//
// Justified and hyphenated, which is the single strongest tell of newspaper setting. The two go
// together and neither works alone: justification without hyphenation opens rivers of white in a
// narrow column, and hyphenation needs the `lang` the editorial layout already puts on <html>.
// The column rule is the hairline a printed page puts between columns so the eye doesn't jump
// track; `columnFill: balance` keeps the last line of each column level, as a page make-up would.
export const body = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  hyphens: 'auto',
  '@media': {
    [WIDE]: {
      // A column WIDTH, not a count. A page make-up sets the measure and takes however many
      // columns fit; a fixed count instead stretches or crushes the measure as the window moves,
      // and at three fixed columns this page was setting justified type 28 characters wide. 18rem
      // lands around 36 characters at the body size, so a wider window buys another column rather
      // than a longer line — which is the actual broadsheet behaviour.
      columnWidth: '18rem',
      columnGap: vars.space[6],
      columnRule: `1px solid ${vars.color.rule}`,
      columnFill: 'balance',
      textAlign: 'justify',
    },
  },
})
globalStyle(`${body} p`, { margin: `0 0 ${vars.space[3]}`, breakInside: 'avoid-column' })
globalStyle(`${body} .bird`, { fontWeight: 500 })
globalStyle(`${body} [data-sci]`, {
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: vars.uline.prose,
  textDecorationColor: vars.color.rule,
})
// THE CITATION TAIL. TodayCard#citation_tail appends `<span class="bullet-cites"><a class="bullet-cite">`
// to a narrated bullet, and this prose arrives as raw HTML — so the anchors are reachable by no other
// rule and, with no global `a {}` anywhere in the app, they fell through to the browser's default
// blue. The house form is the quiet mono source mark (cf. `.today-sources a`): brackets, no underline,
// a hairline that darkens on hover. The brackets are pseudo-elements so the server's markup and the
// reader's copied text stay clean.
globalStyle(`${body} .bullet-cites`, {
  whiteSpace: 'nowrap', // a mark never wraps away from the sentence it cites
})
globalStyle(`${body} .bullet-cite`, {
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  ...sourceMark,
})
globalStyle(`${body} .bullet-cite::before`, { content: '"["' })
globalStyle(`${body} .bullet-cite::after`, { content: '"]"' })
globalStyle(`${body} .bullet-cite:hover`, sourceMarkHover)
globalStyle(`${body} .bullet-cite:focus-visible`, focusRing)
// The tailpiece: the day's hero bird as a small monochrome line vignette (Vignette), centred to
// close the lead like a printer's mark. Black-on-white art, so it inverts to white on the dark
// paper exactly as the cuckoo mark does.
export const tailpiece = style({ margin: `${vars.space[6]} 0 0`, textAlign: 'center' })
globalStyle(`${tailpiece} img`, {
  width: 'auto',
  height: '84px',
  display: 'inline-block',
  mixBlendMode: 'multiply',
})
globalStyle(`:root[data-theme="dark"] ${tailpiece} img`, { filter: 'invert(1)', mixBlendMode: 'screen' })

// The LORE's own mark: one named place reduced to a line vignette (PlaceVignette), opening a lore
// block the way the bird's mark closes the lead. Smaller than the tailpiece — a device beside a
// paragraph, not a picture of its own. Same black-on-white art, so it inverts on the dark paper.
export const placeMark = style({ margin: `0 0 ${vars.space[2]}` })
globalStyle(`${placeMark} img`, {
  width: 'auto',
  height: '56px',
  display: 'block',
  mixBlendMode: 'multiply',
})
globalStyle(`:root[data-theme="dark"] ${placeMark} img`, { filter: 'invert(1)', mixBlendMode: 'screen' })

export const drop = style({})
globalStyle(`${drop}::first-letter`, {
  fontSize: vars.fs.dropcap,
  lineHeight: 0.82,
  float: 'left',
  fontWeight: 500,
  paddingRight: vars.space[2],
  paddingTop: vars.space[1],
})

// ---- Rail sections (record / day-in-birds / calendar) ------------------
// The shared section head, over the Journal's own stronger rule — this is a newspaper column
// head, and the ink rule is the deliberate variation. Every rail section wears it, including
// the calendar, which used to head itself with a rule-less rubric for no reason.
export const railHead = style([
  sectionHead,
  {
    marginBottom: vars.space[3],
    paddingBottom: vars.space[2],
    borderBottom: `1px solid ${vars.color.ink}`,
  },
])
// The station record — ruled rows, mono figures (a record strip, NOT KPI cards).
export const record = style({ margin: 0 })
globalStyle(`${record} > div`, {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: vars.space[3],
  padding: `${vars.space[2]} 0`,
  borderBottom: `1px solid ${vars.color.rule}`,
})
globalStyle(`${record} dt`, {
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.space[2],
  fontFamily: vars.font.mono,
  fontWeight: 500,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  textTransform: 'uppercase',
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
})
globalStyle(`${record} dd`, {
  margin: 0,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.ink,
  textAlign: 'right',
})
export const recIcon = style({
  fontSize: vars.mark.icon,
  color: vars.color.inkSoft,
  lineHeight: 1,
  flex: 'none',
})
// A measured quantity's unit — km/h, mm, °C, h. It rode on a bare <small>, so the browser's
// own 0.8em decided its size and it read differently beside every figure.
export const unit = style({ fontSize: vars.ms.sm, color: vars.color.inkSoft })
export const recWord = style({ textTransform: 'capitalize' })
export const recNameFig = style({ display: 'flex', gap: vars.space[3], alignItems: 'baseline' })
export const recBird = style([
  birdNameRow,
  {
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    selectors: {
      '&:hover': { textDecoration: 'underline', textUnderlineOffset: vars.uline.mark },
      '&:focus-visible': focusRing,
    },
  },
])

export const spark = style({ margin: `${vars.space[4]} 0 0` })
globalStyle(`${spark} figcaption`, {
  fontFamily: vars.font.mono,
  fontWeight: 500,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  textTransform: 'uppercase',
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
  marginBottom: vars.space[2],
})
globalStyle(`${spark} svg`, { width: '100%', height: '44px', display: 'block' })
globalStyle(`${spark} .fill`, { fill: vars.color.paper2 })
globalStyle(`${spark} .line`, { fill: 'none', stroke: vars.color.ink2, strokeWidth: 1.5 })

export const railList = style({ listStyle: 'none', margin: 0, padding: 0 })
export const railBird = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: vars.space[3],
  width: '100%',
  appearance: 'none',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  padding: `${vars.space[2]} 0`,
  borderBottom: `1px solid ${vars.color.rule}`,
  selectors: { '&:hover': { background: vars.color.paper2 }, '&:focus-visible': focusRing },
})
export const railBirdName = birdNameRow
export const railBirdCount = style([reading, { color: vars.color.inkSoft }])
// The rail's expander. It was a bare <p> stating a number the reader could do nothing with; it is a
// button now, so it carries the same reset the other rail buttons do.
export const railMore = style({ marginTop: vars.space[2] })

// ---- Below-the-fold bands: rubric + shared credit ----------------------
export const rubric = style([kickerBase, { margin: `0 0 ${vars.space[2]}` }])
// A section break. Heavier than a hairline so the page reads as ruled-off departments rather than
// one continuous scroll — the below-the-fold equivalent of the folio rule up top.
export const band = style({
  paddingTop: vars.space[5],
  marginTop: vars.space[2],
  borderTop: `2px solid ${vars.color.ink}`,
})
export const sectionTitle = style({
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs.xl,
  lineHeight: vars.lh.snug,
  color: vars.color.ink,
  margin: `0 0 ${vars.space[3]}`,
  textWrap: 'balance',
})
export const prose = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  maxWidth: vars.measure.prose,
  margin: `0 0 ${vars.space[3]}`,
})
// Below the fold the page was setting one narrow ribbon of prose down the left of a page-wide
// field of white: the lead read as a broadsheet and everything under it read as a blog post. This
// puts the retrieved story and the day's lore into the same ruled, justified columns the lead uses,
// so the page is columned from the folio rule to the footer. Applied to the prose element ITSELF,
// not the section — a rubric and a credit line are labels and belong outside the flow.
//
// Declared AFTER `prose` on purpose: it has to beat that rule's single-column `maxWidth`, and with
// equal specificity the later declaration is the one that wins. Moving this block up the file
// silently returns the bands to one column.
export const bandCols = style({
  hyphens: 'auto',
  '@media': {
    [WIDE]: {
      maxWidth: 'none',
      columnWidth: '18rem',
      columnGap: vars.space[6],
      columnRule: `1px solid ${vars.color.rule}`,
      textAlign: 'justify',
    },
  },
})

export const verse = style({
  fontFamily: vars.font.serif,
  fontStyle: 'italic',
  fontSize: vars.fs.lg,
  lineHeight: vars.lh.snug,
  color: vars.color.ink,
  borderLeft: `2px solid ${vars.color.rule}`,
  paddingLeft: vars.space[4],
  margin: `${vars.space[3]} 0`,
  whiteSpace: 'pre-line',
})
export const credit = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  color: vars.color.inkSoft,
  margin: `${vars.space[2]} 0 0`,
})
globalStyle(`${credit} a`, { ...sourceMark })
globalStyle(`${credit} a:hover`, sourceMarkHover)
globalStyle(`${credit} a:focus-visible`, focusRing)
// The dúchas link line and the art plate's treatment note are the same source-credit role,
// down to the last value; they are the same rule now rather than three copies of it.
export const dlink = credit

// ---- Art & archaeology plate ------------------------------------------
export const art = style({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: vars.space[5],
  '@media': {
    [WIDE]: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: vars.space[6], alignItems: 'start' },
  },
})
export const artPlate = style({ margin: 0 })
globalStyle(`${artPlate} img`, {
  width: '100%',
  height: 'auto',
  display: 'block',
  boxShadow: vars.shadow.edge,
})
export const artTreatment = credit
export const artCulture = style({ color: vars.color.inkSoft, fontStyle: 'italic', fontWeight: 400 })

// ---- Place lore (carries a silent day) --------------------------------
export const placeLore = style({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: vars.space[6],
  '@media': { [WIDE]: { gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)' } },
})
export const placeLoreAside = style({
  '@media': { [WIDE]: { borderLeft: `1px solid ${vars.color.rule}`, paddingLeft: vars.space[5] } },
})

// ---- Lore & wisdom -----------------------------------------------------
// A bare <figure> carries the UA's `margin: 1em 40px`, so every lore item sat indented out of
// line with the rubric above it — an accident, not a design. Reset it explicitly.
export const loreItem = style({ overflow: 'hidden', margin: `0 0 ${vars.space[5]}` })
// The shared state line (§8) — an empty Journal now says "there is nothing here" in the same
// voice as an empty Stats period, an empty Directory and a quiet Live.
export const empty = stateLine
